import { AccredibleObjectWithId } from '@accredible-frontend-v2/models';
import { AccredibleKeyboardFocusService } from '@accredible-frontend-v2/services/keyboard-focus';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { AccredibleKey } from '@accredible-frontend-v2/utils/key-enum';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';

const TRANSLATION_PREFIX = 'base-select-input.';
let nextUniqueId = 0;

@UntilDestroy()
@Component({
  template: ``,
  // We are not using ChangeDetectionStrategy.OnPush because this input component
  // does not detect changes when formGroup.markAllAsTouched() is triggered
})
export class AccredibleBaseSelectInputComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  @Input()
  formControlRef: UntypedFormControl;
  @Input()
  label: string;
  @Input()
  hideLabel: boolean;
  @Input()
  hint: string;
  @Input()
  placeholder = '';
  @Input()
  dynamic = false;
  @Input()
  loading = false;
  @Input()
  options: AccredibleObjectWithId[];
  @Input()
  labelKey: string;
  @Input()
  ariaLiveActive = false;
  @Input()
  ariaLiveDuration = 1500;

  @Output()
  search = new EventEmitter<string>();
  @Output()
  select = new EventEmitter<AccredibleObjectWithId>();
  @Output()
  clear = new EventEmitter();

  @ViewChild('inputEl')
  inputEl: ElementRef<HTMLInputElement>;
  @ViewChild('availableOptionsContainer')
  availableOptionsContainer: ElementRef<HTMLElement>;

  focused = false;
  loaded = false;
  clickInside = false;
  term$ = new BehaviorSubject<string>('');
  cdkKeyboardFocused = false;
  markedOptionIndex = -1;

  get availableOptions(): AccredibleObjectWithId[] {
    return this._availableOptions;
  }

  set availableOptions(options: AccredibleObjectWithId[]) {
    this._availableOptions = options;
    if (this.ariaLiveActive) {
      this._liveAnnouncer.announce(
        this._language.translate(TRANSLATION_PREFIX + 'options') +
          this.availableOptions.map((option) => option[this.labelKey]).join(','),
        this.ariaLiveDuration,
      );
    }
  }

  uniqueIdIndex: number;

  private _availableOptions: AccredibleObjectWithId[] = [];

  constructor(
    private readonly _keyboardFocus: AccredibleKeyboardFocusService,
    private readonly _liveAnnouncer: LiveAnnouncer,
    private readonly _language: AccredibleLanguageService,
  ) {
    this.uniqueIdIndex = nextUniqueId++;
  }

  @HostListener('document:click', ['$event'])
  onClickOut(): void {
    if (!this.clickInside) {
      this.inputBlurred();
    }

    this.clickInside = false;
  }

  ngOnInit(): void {
    this._handleSearchTermChange();
  }

  ngOnChanges(): void {
    this._filterOptions();

    if (this.dynamic && this.inputEl?.nativeElement.value) {
      this.loaded = true;
    }
  }

  ngAfterViewInit(): void {
    this._keyboardFocus.startFocusMonitoring(this.inputEl);
  }

  ngOnDestroy(): void {
    this._keyboardFocus.stopFocusMonitoring(this.inputEl);
  }

  inputBlurred(): void {
    this.focused = false;
    this.inputEl.nativeElement.blur();
    this.inputEl.nativeElement.value = '';
    this._filterOptions();
  }

  onInputClick(): void {
    this.inputFocused(null);
    this.clickInside = true;
    this.inputEl.nativeElement.focus();
  }

  inputFocused(event: Event): void {
    this.cdkKeyboardFocused =
      event && (event.target as HTMLElement).classList.contains('cdk-keyboard-focused');

    this.focused = true;
    this.formControlRef?.markAsTouched();
  }

  onSearch(term: string): void {
    this.term$.next(term);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (
      this.availableOptions.length > 0 &&
      [AccredibleKey.ARROW_UP, AccredibleKey.ARROW_DOWN].includes(<AccredibleKey>event.code)
    ) {
      event.preventDefault();
      this._decideMarkedOption(<AccredibleKey>event.code);
    }

    if (event.code === AccredibleKey.ENTER) {
      event.preventDefault();
      this.availableOptions[this.markedOptionIndex] &&
        this.selectOption(null, this.availableOptions[this.markedOptionIndex]);
    }

    if (event.code === AccredibleKey.ESCAPE || event.code === AccredibleKey.TAB) {
      this.inputBlurred();
    }
  }

  selectOption(event: Event, option: AccredibleObjectWithId): void {}

  clearSelected(): void {
    this.formControlRef.setValue(null);
    this.clear.emit();
  }

  protected _filterOptions(): void {
    const availableOptions = this.dynamic ? this.options : this._filterOptionsBySearchTerm();
    this.availableOptions = this._hideSelectedOptions(availableOptions);
    this.markedOptionIndex = -1;
  }

  protected _decideMarkedOption(code: AccredibleKey): void {
    switch (code) {
      case AccredibleKey.ARROW_UP:
        this.markedOptionIndex =
          this.markedOptionIndex <= 0
            ? this.availableOptions.length - 1
            : this.markedOptionIndex - 1;
        break;

      case AccredibleKey.ARROW_DOWN:
        this.markedOptionIndex =
          this.markedOptionIndex >= this.availableOptions.length - 1
            ? 0
            : this.markedOptionIndex + 1;
        break;
    }

    this._updateScrollPosition();
  }

  protected _hideSelectedOptions(
    availableOptions: AccredibleObjectWithId[],
  ): AccredibleObjectWithId[] {
    return [];
  }

  private _filterOptionsBySearchTerm(
    term: string = this.inputEl?.nativeElement.value,
  ): AccredibleObjectWithId[] {
    return !term || !term.trim()
      ? this.options
      : this.options.filter((option: AccredibleObjectWithId) =>
          option[this.labelKey].toString().toLowerCase().includes(term.trim().toLowerCase()),
        );
  }

  private _handleSearchTermChange(): void {
    this.term$
      .pipe(untilDestroyed(this), distinctUntilChanged(), skip(1), debounceTime(500))
      .subscribe((term) => {
        if (this.dynamic) {
          this.loaded = false;
          this.search.emit(term.trim());
        } else {
          this._filterOptions();
        }
      });
  }

  private _updateScrollPosition(): void {
    const highlightedItem =
      this.availableOptionsContainer.nativeElement.children[this.markedOptionIndex];
    const highlightedItemTop = (highlightedItem as HTMLElement).offsetTop;
    const highlightedItemBottom =
      (highlightedItem as HTMLElement).offsetTop + (highlightedItem as HTMLElement).offsetHeight;
    const scrollContainerBottom = this.availableOptionsContainer.nativeElement.offsetHeight;
    const scrollPosition = this.availableOptionsContainer.nativeElement.scrollTop;

    if (highlightedItemBottom > scrollContainerBottom + scrollPosition) {
      this.availableOptionsContainer.nativeElement.scrollTop =
        highlightedItemBottom - scrollContainerBottom;
    } else if (highlightedItemTop < scrollPosition) {
      this.availableOptionsContainer.nativeElement.scrollTop = highlightedItemTop;
    }
  }
}
